// GLB文件测试工具
import * as THREE from 'three'
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js'

export class GLBTestSuite {
  constructor() {
    this.loader = new GLTFLoader()
  }

  /**
   * 测试GLB文件加载
   */
  async testGLBLoad(modelUrl = '/models/default.glb') {
    return new Promise((resolve, reject) => {
      console.log('🧪 开始测试GLB文件加载:', modelUrl)
      
      this.loader.load(
        modelUrl,
        (gltf) => {
          console.log('✅ GLB加载成功！')
          
          const analysis = this.analyzeGLTF(gltf)
          console.log('📊 GLB文件分析:', analysis)
          
          resolve({
            success: true,
            gltf: gltf,
            analysis: analysis
          })
        },
        (progress) => {
          const percent = Math.round((progress.loaded / progress.total) * 100)
          console.log(`📥 加载进度: ${percent}% (${progress.loaded}/${progress.total})`)
        },
        (error) => {
          console.error('❌ GLB加载失败:', error)
          reject({
            success: false,
            error: error
          })
        }
      )
    })
  }

  /**
   * 分析GLTF内容
   */
  analyzeGLTF(gltf) {
    const analysis = {
      scenes: gltf.scenes?.length || 0,
      animations: gltf.animations?.length || 0,
      cameras: gltf.cameras?.length || 0,
      materials: [],
      meshes: [],
      lights: [],
      totalTriangles: 0
    }

    if (gltf.scene) {
      gltf.scene.traverse((child) => {
        if (child.isMesh) {
          analysis.meshes.push({
            name: child.name || 'unnamed',
            type: child.type,
            hasGeometry: !!child.geometry,
            hasMaterial: !!child.material,
            triangles: child.geometry?.attributes?.position ? 
              Math.floor(child.geometry.attributes.position.count / 3) : 0
          })

          if (child.geometry?.attributes?.position) {
            analysis.totalTriangles += Math.floor(child.geometry.attributes.position.count / 3)
          }

          if (child.material) {
            const materialInfo = {
              name: child.material.name || 'unnamed',
              type: child.material.type,
              color: child.material.color?.getHexString?.() || 'unknown',
              transparent: child.material.transparent,
              opacity: child.material.opacity
            }
            analysis.materials.push(materialInfo)
          }
        } else if (child.isLight) {
          analysis.lights.push({
            name: child.name || 'unnamed',
            type: child.type,
            intensity: child.intensity
          })
        }
      })
    }

    // 获取边界框信息
    if (gltf.scene) {
      const box = new THREE.Box3().setFromObject(gltf.scene)
      const size = box.getSize(new THREE.Vector3())
      const center = box.getCenter(new THREE.Vector3())
      
      analysis.boundingBox = {
        size: {
          x: parseFloat(size.x.toFixed(3)),
          y: parseFloat(size.y.toFixed(3)),
          z: parseFloat(size.z.toFixed(3))
        },
        center: {
          x: parseFloat(center.x.toFixed(3)),
          y: parseFloat(center.y.toFixed(3)),
          z: parseFloat(center.z.toFixed(3))
        },
        maxDimension: Math.max(size.x, size.y, size.z)
      }
    }

    return analysis
  }

  /**
   * 快速验证GLB文件
   */
  async quickTest() {
    try {
      const result = await this.testGLBLoad()
      
      const issues = []
      
      if (result.analysis.meshes.length === 0) {
        issues.push('⚠️ 没有找到网格(mesh)对象')
      }
      
      if (result.analysis.totalTriangles === 0) {
        issues.push('⚠️ 没有找到几何数据')
      }
      
      if (result.analysis.boundingBox?.maxDimension === 0) {
        issues.push('⚠️ 模型尺寸为零')
      }

      if (issues.length > 0) {
        console.warn('GLB文件问题:')
        issues.forEach(issue => console.warn(issue))
      } else {
        console.log('✅ GLB文件验证通过！')
      }

      return {
        success: true,
        issues: issues,
        analysis: result.analysis
      }

    } catch (error) {
      console.error('GLB测试失败:', error)
      return {
        success: false,
        error: error
      }
    }
  }

  /**
   * 创建简单的测试场景
   */
  createTestScene() {
    const scene = new THREE.Scene()
    scene.background = new THREE.Color(0x808080)

    // 添加光照
    const ambientLight = new THREE.AmbientLight(0xffffff, 0.6)
    scene.add(ambientLight)

    const directionalLight = new THREE.DirectionalLight(0xffffff, 0.8)
    directionalLight.position.set(1, 1, 1)
    scene.add(directionalLight)

    return scene
  }
}

// 导出测试实例
export const glbTestSuite = new GLBTestSuite()

// 在开发环境中自动运行GLB测试
if (process.env.NODE_ENV === 'development') {
  // 延迟运行，避免在模块加载时立即执行
  setTimeout(async () => {
    console.log('🔧 开发模式：运行GLB文件测试...')
    await glbTestSuite.quickTest()
  }, 3000)
}